import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Seo from '../components/Seo';
import { Phone, Mountain, Users, Droplets, Ruler, Zap, ChevronRight, ChevronLeft, X, Check, Sparkles, Grid, Smartphone } from 'lucide-react';
import { CONTACT } from '../data/constants';
import { UNIVERSAL_ROCKS_WATERFALLS, UNIVERSAL_ROCKS_PANELS } from '../data/outdoorCatalogs';
import CallForPricingGrid from '../components/CallForPricingGrid';
import { VideoBlock } from '../components/VideoBlock';
import { REELS } from '../data/reels';
import {
  NATURAL_SPAS_CATEGORIES,
  NATURAL_SPAS_PRODUCTS,
  NATURAL_SPAS_HERO,
  NATURAL_SPAS_FEATURE,
  NATURAL_SPAS_LIFESTYLE,
} from '../data/naturalSpas';

const sortByPrice = (list) => [...list].sort((a, b) => a.priceValue - b.priceValue);

// ---- Detail Modal ----
const ProductModal = ({ product, onClose }) => {
  const [imgIdx, setImgIdx] = useState(0);
  if (!product) return null;
  const images = product.images || [];
  const next = () => setImgIdx((i) => (i + 1) % images.length);
  const prev = () => setImgIdx((i) => (i - 1 + images.length) % images.length);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
        data-testid="natural-spa-modal"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', duration: 0.4 }}
          className="bg-white rounded-2xl max-w-5xl w-full my-8 overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between bg-gradient-to-r from-[#0E3D95] to-[#1a4d8a] text-white p-5">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/70 font-bold">Natural Rock Spas</p>
              <h2 className="font-['Barlow_Condensed'] text-2xl md:text-4xl font-black uppercase">{product.name}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center transition-colors"
              aria-label="Close"
              data-testid="natural-spa-modal-close"
            >
              <X size={22} />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-0">
            {/* Gallery */}
            <div className="relative bg-slate-100 aspect-square md:aspect-auto md:min-h-[420px]">
              {images.length > 0 && (
                <img
                  src={images[imgIdx]}
                  alt={`${product.name} - ${imgIdx + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              )}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute top-1/2 left-2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    onClick={next}
                    className="absolute top-1/2 right-2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center"
                    aria-label="Next image"
                  >
                    <ChevronRight size={22} />
                  </button>
                  <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-bold px-2 py-1 rounded">
                    {imgIdx + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {/* Details */}
            <div className="p-6 md:p-8 max-h-[70vh] overflow-y-auto">
              <p className="text-lg md:text-xl font-bold text-[#F88200] mb-1">Call or email for pricing</p>
              <p className="text-xs text-slate-500 uppercase tracking-widest font-bold mb-4">Starting At &middot; Call for Naples Pricing</p>

              <p className="text-slate-700 leading-relaxed mb-5">{product.longDescription || product.description}</p>

              {/* Quick specs grid */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500 uppercase font-bold mb-1"><Users size={14} className="text-[#F88200]" /> Seats</div>
                  <p className="font-bold text-[#0E3D95]">{product.seats}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500 uppercase font-bold mb-1"><Droplets size={14} className="text-[#F88200]" /> Water</div>
                  <p className="font-bold text-[#0E3D95]">{product.gallons} gal</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-xs text-slate-500 uppercase font-bold mb-1"><Zap size={14} className="text-[#F88200]" /> Jets</div>
                  <p className="font-bold text-[#0E3D95]">{product.jets}</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 col-span-2">
                  <div className="flex items-center gap-2 text-xs text-slate-500 uppercase font-bold mb-1"><Ruler size={14} className="text-[#F88200]" /> Dimensions</div>
                  <p className="font-semibold text-[#0E3D95] text-sm">{product.dimensions}</p>
                </div>
              </div>

              {/* Colors */}
              {product.colors && product.colors.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-2">Color Options</p>
                  <div className="flex flex-wrap gap-2">
                    {product.colors.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 bg-slate-100 border border-slate-300 px-3 py-1 rounded-full text-sm font-medium text-slate-700">
                        <span className={`w-3 h-3 rounded-full ${
                          c.includes('Black') ? 'bg-slate-800' :
                          c.includes('Castle') ? 'bg-slate-400' : 'bg-amber-800'
                        }`} />
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Features */}
              {product.features && (
                <div className="mb-5">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-bold mb-2">Unit Includes</p>
                  <ul className="space-y-1.5">
                    {product.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check size={14} className="text-[#F88200] mt-1 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTAs */}
              <div className="space-y-2 pt-4 border-t border-slate-200">
                <a
                  href={`tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="natural-spa-call-btn"
                >
                  <Phone size={18} /> Call {CONTACT.phone}
                </a>
                <Link to="/contact" onClick={onClose} className="btn-secondary w-full flex items-center justify-center gap-2">
                  Get a Quote
                </Link>
                <Link
                  to={`/ar-visualizer?product=${encodeURIComponent(product.id)}`}
                  onClick={onClose}
                  className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#F88200] to-[#ff9d3c] hover:from-[#e07600] hover:to-[#F88200] text-white font-bold py-3 px-5 rounded-lg shadow-md hover:shadow-lg transition-colors"
                  data-testid="natural-spa-modal-ar-btn"
                  aria-label={`See ${product.name} in your space with AR`}
                >
                  <Smartphone size={18} />
                  See it in Your Space (AR)
                </Link>
                {product.specSheet && (
                  <a
                    href={product.specSheet}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 text-[#0E3D95] hover:text-[#F88200] font-semibold text-sm py-2"
                  >
                    Download Spec Sheet (PDF) →
                  </a>
                )}
                <p className="text-xs text-slate-400 italic text-center pt-2">
                  Contact us for pricing — does not include shipping, utilities, or installation.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ---- Product Card ----
const ProductCard = ({ product, onClick }) => (
  <motion.div
    whileHover={{ y: -4 }}
    className="relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow text-left group"
    data-testid={`natural-spa-card-${product.id}`}
  >
    {/* AR Quick-Action Button - top right of card */}
    <Link
      to={`/ar-visualizer?product=${encodeURIComponent(product.id)}`}
      onClick={(e) => e.stopPropagation()}
      className="absolute top-2 right-2 z-20 inline-flex items-center gap-1 bg-[#F88200] hover:bg-[#e07600] text-white text-[10px] md:text-xs font-bold uppercase tracking-wide px-2 py-1 md:px-2.5 md:py-1.5 rounded-full shadow-md transition-colors"
      aria-label={`See ${product.name} in your space with AR`}
      data-testid={`natural-spa-ar-btn-${product.id}`}
      title="See it in your space with AR"
    >
      <Smartphone size={12} />
      <span>AR</span>
    </Link>

    <button
      onClick={onClick}
      className="w-full text-left"
    >
      <div className="relative aspect-[4/3] bg-slate-100 overflow-hidden">
        <img
          src={product.images[0]}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute top-2 left-2 bg-[#0E3D95] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
          Natural Rock Spas
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold uppercase text-[#0E3D95] leading-none mb-1">{product.name}</h3>
        <p className="text-xs text-slate-500 mb-2 line-clamp-2">{product.description}</p>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div>
            <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Starting At</p>
            <p className="text-sm font-black text-[#F88200]">Call or email for pricing</p>
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-bold text-[#0E3D95] group-hover:text-[#F88200] uppercase tracking-wider">
            View <ChevronRight size={14} />
          </span>
        </div>
        <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1"><Users size={12} /> {product.seats}</span>
          <span className="inline-flex items-center gap-1"><Droplets size={12} /> {product.gallons} gal</span>
        </div>
      </div>
    </button>

    {/* Full-width AR CTA at the bottom of the card */}
    <Link
      to={`/ar-visualizer?product=${encodeURIComponent(product.id)}`}
      className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-[#F88200] to-[#ff9d3c] hover:from-[#e07600] hover:to-[#F88200] text-white font-bold text-xs md:text-sm uppercase tracking-wider py-2.5 transition-colors"
      data-testid={`natural-spa-ar-cta-${product.id}`}
      aria-label={`See ${product.name} in your space with AR`}
    >
      <Smartphone size={16} />
      See it in Your Space (AR)
    </Link>
  </motion.div>
);

const NaturalSpasPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const allCategories = [
    { id: 'all', name: 'All Products', count: NATURAL_SPAS_PRODUCTS.length },
    ...NATURAL_SPAS_CATEGORIES.map((c) => ({
      id: c.id,
      name: c.name,
      count: NATURAL_SPAS_PRODUCTS.filter((p) => p.category === c.id).length,
    })),
  ];

  const filtered = useMemo(() => {
    const list = activeCategory === 'all'
      ? NATURAL_SPAS_PRODUCTS
      : NATURAL_SPAS_PRODUCTS.filter((p) => p.category === activeCategory);
    return sortByPrice(list);
  }, [activeCategory]);

  return (
    <>
      <Seo page="/natural-rock-spas" />

      <div className="pt-28 md:pt-36 pb-20" style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #E0FFFF 20%, #AFEEEE 50%, #98FB98 80%, #F0FFF0 100%)'
      }} data-testid="natural-spas-page">
        {/* Hero */}
        <section className="relative h-[55vh] min-h-[400px] flex items-center justify-center overflow-hidden mb-12 md:mb-16">
          <div className="absolute inset-0">
            <img src={NATURAL_SPAS_HERO} alt="Natural Rock Spas backyard" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0E3D95]/70 via-[#0E3D95]/40 to-[#0E3D95]/80" />
          </div>
          <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-2 mb-4"
            >
              <Mountain className="text-[#FFD700]" size={24} />
              <span className="text-sm uppercase tracking-widest text-[#FFD700] font-semibold">Authorized Naples, FL Dealer</span>
              <Mountain className="text-[#FFD700]" size={24} />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold uppercase mb-5 leading-none"
            >
              Natural <span className="text-[#F88200]">Rock</span> Spas
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-2xl text-slate-100 max-w-3xl mx-auto mb-6"
            >
              Stunningly realistic. Lightweight. American-made. From subtle backyard spas that blend into any landscape — to the most extravagant rock-pool oasis you can imagine.
            </motion.p>
            <motion.a
              href={`tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            >
              <Phone size={20} /> Call {CONTACT.phone}
            </motion.a>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Why Natural Rock Spas */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-4 mb-12 md:mb-16"
          >
            {[
              { title: 'Lightweight', text: 'Easier to install and cheaper to ship than real rock. Set up faster, move it later.' },
              { title: 'American Made', text: 'Manufactured in the USA — handcrafted and meticulously textured by skilled artisans.' },
              { title: 'Stunningly Realistic', text: 'Indistinguishable from natural stone at first glance. Custom-built to fit your vision.' },
            ].map((item) => (
              <div key={item.title} className="bg-white/80 backdrop-blur-sm border border-emerald-200 rounded-xl p-5 shadow-sm">
                <div className="w-12 h-12 bg-[#F88200] rounded-full flex items-center justify-center mb-3">
                  <Sparkles className="text-white" size={20} />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-[#0E3D95] mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.text}</p>
              </div>
            ))}
          </motion.div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 md:gap-3 mb-6">
            {allCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 md:px-5 md:py-3 font-semibold text-sm md:text-base transition-all rounded-lg ${
                  activeCategory === cat.id
                    ? 'bg-[#0E3D95] text-white shadow-lg'
                    : 'bg-white/80 text-[#0E3D95] hover:bg-[#F88200] hover:text-white border border-slate-200'
                }`}
                data-testid={`ns-category-btn-${cat.id}`}
              >
                {cat.id === 'all' && <Grid size={16} />}
                <span>{cat.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${activeCategory === cat.id ? 'bg-white/20' : 'bg-slate-100'}`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>

          {/* Category description */}
          {activeCategory !== 'all' && (
            <p className="text-slate-600 max-w-3xl mb-6">
              {NATURAL_SPAS_CATEGORIES.find((c) => c.id === activeCategory)?.description}
            </p>
          )}

          {/* Product Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {filtered.map((p) => (
              <ProductCard key={p.id} product={p} onClick={() => setSelectedProduct(p)} />
            ))}
          </div>

          {/* Universal Rocks Video */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 mb-4"
            data-testid="universal-rocks-video"
          >
            <div className="rounded-2xl overflow-hidden shadow-xl bg-black">
              <video
                autoPlay
                muted
                loop
                playsInline
                controls
                className="w-full h-auto max-h-[70vh] object-contain bg-black"
              >
                <source src="https://customer-assets-jt897jd0.emergentagent.net/job_product-pricing-27/artifacts/y8bie8qd_Universal%20Rocks%20Hot%20Tub%20Video%20Leisure%20Wellnes%20Naples%20Florida.mp4" type="video/mp4" />
              </video>
            </div>
          </motion.div>

          {/* Universal Rocks — Pool Waterfall Kits */}
          <CallForPricingGrid
            testId="universal-rocks-waterfalls"
            eyebrow="Universal Rocks · Lightweight Artificial Rock"
            title="Pool Waterfall Kits"
            subtitle="Turn your backyard into an oasis. Stunningly realistic, lightweight faux-rock waterfall kits that connect to your existing pool equipment. Click any kit for photos & details."
            items={UNIVERSAL_ROCKS_WATERFALLS}
          />

          {/* Universal Rocks — Rock Wall / Faux Stone Panels */}
          <CallForPricingGrid
            testId="universal-rocks-panels"
            eyebrow="Universal Rocks · Made in the USA"
            title="Rock Wall & Faux Stone Panels"
            subtitle="Realistic faux stone and wood panels for feature walls, water features and themed spaces — indoor, outdoor & underwater rated."
            items={UNIVERSAL_ROCKS_PANELS}
          />

          {/* Lifestyle / CTA */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-xl mb-12"
          >
            <div className="aspect-[4/3] md:aspect-auto">
              <img src={NATURAL_SPAS_LIFESTYLE} alt="Natural Rock Spa lifestyle" className="w-full h-full object-cover" />
            </div>
            <div className="bg-gradient-to-br from-[#0E3D95] to-[#1a4d8a] text-white p-8 md:p-12 flex flex-col justify-center">
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold uppercase leading-none mb-4">
                Lightweight. Durable. <span className="text-[#F88200]">Stunningly Realistic.</span>
              </h2>
              <p className="text-white/90 leading-relaxed mb-6">
                At first glance, it&apos;s almost impossible to distinguish these lightweight faux rocks from the real thing. Each piece is handcrafted and meticulously textured to look like real rocks found in nature. Unlike real rock, they&apos;re easier to install, cost less to ship, and ship complete and fully customized — so you can build the backyard oasis others only dream about.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href={`tel:${CONTACT.phone.replace(/[^0-9]/g, '')}`} className="btn-primary inline-flex items-center gap-2">
                  <Phone size={18} /> Call {CONTACT.phone}
                </a>
                <Link to="/contact" className="border-2 border-white text-white hover:bg-white/10 px-6 py-3 font-['Barlow_Condensed'] font-bold uppercase tracking-wider transition-colors rounded">
                  Get a Quote
                </Link>
              </div>
            </div>
          </motion.section>

          <VideoBlock eyebrow="See It Installed" reel={REELS.rocks} testId="natural-rocks-video" />

          {/* Dealer note */}
          <div className="text-center text-sm text-slate-500 max-w-3xl mx-auto">
            <p className="italic">
              Leisure Wellness Naples is an authorized Natural Rock Spas dealer serving Naples and Southwest Florida.
              All MSRP pricing shown — call us for delivery, installation, and bundle savings.
            </p>
          </div>
        </div>
      </div>

      <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
    </>
  );
};

export default NaturalSpasPage;
